import { inject, Injectable, signal } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  ApiCacheService,
  BaseHttpService,
  StorageService,
  ENV_CONFIG,
} from '@chefami-monorepo/data-access';
import type { CheckoutEnvironment } from '../../environments/environment.interface';
import {
  OrderDetails,
  ValidateZipcodeResponse,
  ClaimCouponResponse,
  CouponClaimType,
  PromoCodeResponse,
  CreateSubscriptionPayload,
  CreateSubscriptionResponse,
  CheckZipDeliveryDayResponse,
  ValidateSubscriptionDataPayload,
  ValidateSubscriptionDataResponse,
} from '../interfaces/order';
import { SiteStorage } from '../constants/global';

const ZIPCODE_CACHE_KEY = 'zipcode_validate';
const NEW_PLAN_CACHE_KEY = 'plan_cache';

import {
  DeliveryDates,
  NewSubscriptionDetailsResponse,
} from '../interfaces/plan';

/**
 * Service handling all new order API calls and sessionStorage-based
 * order state management for the checkout flow.
 */
@Injectable({ providedIn: 'root' })
export class NewOrderService {
  private readonly apiCache = inject(ApiCacheService);
  private readonly baseHttp = inject(BaseHttpService);
  private readonly storage = inject(StorageService);
  private readonly env = inject<CheckoutEnvironment>(ENV_CONFIG);

  /** Reactive mirror of the stored `delivery` key, kept in sync with sessionStorage. */
  private readonly deliveryKey = signal<number | undefined>(
    this.getOrderStorageData()?.delivery,
  );
  readonly selectedDeliveryKey = this.deliveryKey.asReadonly();

  // ─── API Methods ──────────────────────────────────────────────

  /**
   * Validates whether delivery is available for the given zip code.
   *
   * Results are cached per zip code via `ApiCacheService` (in-memory,
   * with in-flight deduplication). Only `0000` and `9001` responses are
   * cached; error codes are never stored.
   *
   * @returns The API response for codes `0000` (delivery available) and
   * `9001` (pickup required), or `null` for any other code.
   */
  validateZipcode(zipcode: string): Observable<ValidateZipcodeResponse | null> {
    return this.apiCache
      .get<ValidateZipcodeResponse>(
        this.env.checkZipCode,
        { zipcode },
        `${ZIPCODE_CACHE_KEY}_${zipcode}`,
        (res) => res.response.code === '0000' || res.response.code === '9001',
      )
      .pipe(
        map((response: ValidateZipcodeResponse) => {
          const code = response.response.code;
          return code === '0000' || code === '9001' ? response : null;
        }),
      );
  }

  // ─── Session Storage helpers ────────────────────────────────────

  /**
   * Merges `data` into the existing order details in sessionStorage.
   * New keys are added; existing keys are overwritten by incoming values.
   */
  updateOrderStorageData(data: OrderDetails): void {
    const existing = this.getOrderStorageData();
    const merged: OrderDetails = { ...existing, ...data };
    this.storage.setItem(SiteStorage.NEWORDERDETAILS, merged, true);
    this.deliveryKey.set(merged.delivery);
  }

  /**
   * Removes specific properties from the stored order details in sessionStorage.
   * @param keys The property names to remove from the stored order data.
   */
  removeOrderStorageFields(...keys: (keyof OrderDetails)[]): void {
    const existing = this.getOrderStorageData();
    if (!existing) return;

    const updated = { ...existing };
    for (const key of keys) {
      delete updated[key];
    }
    this.storage.setItem(SiteStorage.NEWORDERDETAILS, updated, true);
    this.deliveryKey.set(updated.delivery);
  }

  /** Reads the stored order details from sessionStorage. */
  getOrderStorageData(): OrderDetails | null {
    return this.storage.getItem<OrderDetails>(
      SiteStorage.NEWORDERDETAILS,
      true,
    );
  }

  /** Removes all order data from sessionStorage. */
  clearOrderStorageData(): void {
    this.storage.removeItem(SiteStorage.NEWORDERDETAILS, true);
    this.deliveryKey.set(undefined);
  }

  /**
   * Claims a coupon, free meal, or referral discount via the appropriate API endpoint.
   * @param token The coupon/referral token or code
   * @param type The claim type determining which endpoint and payload shape to use
   */
  claimCoupon(
    token: string,
    type: CouponClaimType,
  ): Observable<ClaimCouponResponse> {
    const url =
      type === 'claim-free-meal'
        ? this.env.claimFreeMeal
        : type === 'claim-coupon'
          ? this.env.claimCoupon
          : this.env.referralCoupon;
    const payload: Record<string, string> =
      type === 'referral' ? { code: token } : { token };
    return this.baseHttp.sendRequest<ClaimCouponResponse>(url, payload);
  }

  applyCoupon(
    zipcode: string,
    coupon_code: string,
  ): Observable<PromoCodeResponse> {
    const url = this.env.applyCoupon;
    const payload = {
      zipcode,
      coupon_code,
    };
    return this.baseHttp.sendRequest<PromoCodeResponse>(url, payload);
  }

  checkZipDeliveryDay(
    oldZipcode: string,
    newZipcode: string,
  ): Observable<CheckZipDeliveryDayResponse> {
    return this.baseHttp.sendRequest<CheckZipDeliveryDayResponse>(
      this.env.checkZipDeliveryDay,
      { old_zipcode: oldZipcode, new_zipcode: newZipcode },
    );
  }

  getPlanDetails(
    zipcode: string,
  ): Observable<NewSubscriptionDetailsResponse | null> {
    return this.apiCache
      .get<NewSubscriptionDetailsResponse>(
        this.env.getNewSubscriptionDetails,
        { zipcode },
        `${NEW_PLAN_CACHE_KEY}_${zipcode}`,
        (res) => res.response.code === '0000' || res.response.code === '1009',
      )
      .pipe(
        map((response) => {
          const code = response.response.code;
          return code === '0000' || code === '1009' ? response : null;
        }),
      );
  }

  createSubscription(
    payload: CreateSubscriptionPayload,
  ): Observable<CreateSubscriptionResponse> {
    return this.baseHttp.sendRequest<CreateSubscriptionResponse>(
      this.env.createSubscription,
      payload,
      undefined,
      undefined,
      true, // Skip global spinner for this request, as the checkout page has its own loading state
    );
  }

  validateSubscriptionData(
    payload: ValidateSubscriptionDataPayload,
  ): Observable<ValidateSubscriptionDataResponse> {
    return this.baseHttp.sendRequest<ValidateSubscriptionDataResponse>(
      this.env.validateSubscriptionData,
      payload,
    );
  }

  resetPlanPage(): void {
    const fieldsToRemove: (keyof OrderDetails)[] = ['product_price'];
    this.removeOrderStorageFields(...fieldsToRemove);
  }

  resetZipCodePage(): void {
    const fieldsToRemove: (keyof OrderDetails)[] = [
      'is_pick_up',
      'pick_up_index',
      'locations',
      'zipcode',
      'address',
      'city',
      'state',
      'delivery_note',
      'formattedAddress',
      'product_price',
    ];
    this.removeOrderStorageFields(...fieldsToRemove);
  }

  /**
   * Resets the stored delivery timestamp in sessionStorage if the current stored delivery date is not present in the provided list of available delivery dates. If no match is found, it sets the delivery date to the first available option.
   * @param deliveryDates
   */
  resetDeliveryTimestamp(deliveryDates: DeliveryDates[]): void {
    if (!deliveryDates.length) return;
    const storedDelivery = this.getOrderStorageData()?.delivery;
    const match = deliveryDates.find((item) => item.key === storedDelivery);
    if (!match) {
      this.updateOrderStorageData({
        delivery: deliveryDates[0]?.key,
      });
    }
  }
}
